/**
 * Unified Media Session Hook
 * 
 * Consolidates useMediaSession.ts (Capacitor plugin) and useNativeMediaSession.ts (Web API)
 * into a single hook that automatically selects the best implementation.
 * 
 * - Native platforms (iOS/Android): Uses @jofr/capacitor-media-session plugin
 * - Web/Desktop: Uses navigator.mediaSession Web API
 * 
 * This provides consistent lock screen controls, notification metadata,
 * and hardware media key support across all platforms.
 */

import { useEffect, useRef, useCallback } from "react";
import type { MediaPlayerInstance } from "@vidstack/react";
import { Capacitor } from "@capacitor/core";
import { MediaSession } from "@jofr/capacitor-media-session";
import { useOs } from "@mantine/hooks";

// ============================================
// Types
// ============================================

export interface MediaSessionMetadata {
	title: string;
	artist?: string;
	album?: string;
	artwork?: Array<{ src: string; sizes?: string; type?: string }>;
}

export interface UseUnifiedMediaSessionReturn {
	/** Update the playback state (playing/paused/none) */
	updatePlaybackState: (state: "playing" | "paused" | "none") => void;
	/** Update media metadata */
	updateMetadata: (metadata: MediaSessionMetadata) => void;
	/** Force update the position state */
	updatePositionState: () => void;
}

// ============================================
// Constants
// ============================================

const DEFAULT_ARTWORK = [
	{ src: "/assets/logo-only.png", sizes: "512x512", type: "image/png" },
];

const DEFAULT_ARTIST = "Akademia LTCA";
const DEFAULT_ALBUM = "Kursy LTCA";

// ============================================
// Platform Detection
// ============================================

function isNativePlatform(): boolean {
	return Capacitor.isNativePlatform();
}

// ============================================
// Main Hook
// ============================================

export function useUnifiedMediaSession(
	player: React.RefObject<MediaPlayerInstance>,
	metadata: MediaSessionMetadata,
	isActive: boolean
): UseUnifiedMediaSessionReturn {
	const cleanupRef = useRef<(() => void) | null>(null);
	const positionIntervalRef = useRef<number | null>(null);
	const metadataRef = useRef<MediaSessionMetadata>(metadata);
	
	const os = useOs();
	const isAndroid = os === "android";
	const isIOS = os === "ios";
	const useCapacitorPlugin = isNativePlatform();
	
	// Keep metadata ref updated
	useEffect(() => {
		metadataRef.current = metadata;
	}, [metadata]);

	// ============================================
	// Capacitor Plugin Implementation (Native)
	// ============================================

	const setupCapacitorMediaSession = useCallback(async () => {
		if (!player.current) return null;
		
		console.log("[UnifiedMediaSession] Setting up Capacitor MediaSession plugin");
		
		try {
			// Set metadata
			await MediaSession.setMetadata({
				title: metadataRef.current.title,
				artist: metadataRef.current.artist || DEFAULT_ARTIST,
				album: metadataRef.current.album || DEFAULT_ALBUM,
				artwork: metadataRef.current.artwork || DEFAULT_ARTWORK,
			});
			
			// Update position state helper
			const updatePosition = async (): Promise<void> => {
				if (!player.current) return;
				
				try {
					const duration = player.current.duration || 0;
					const position = player.current.currentTime || 0;
					const playbackRate = player.current.playbackRate || 1;
					
					if (duration > 0) {
						await MediaSession.setPositionState({
							duration,
							position,
							playbackRate,
						});
					}
				} catch (error) {
					console.warn("[UnifiedMediaSession] Position update failed:", error);
				}
			};
			
			// Seek time depends on platform
			const seekTime = isAndroid ? 15 : 10;
			
			// Setup action handlers
			await MediaSession.setActionHandler(
				{ action: "play" },
				() => {
					console.log("[UnifiedMediaSession] Play action");
					player.current?.play()
						.then(() => {
							MediaSession.setPlaybackState({ playbackState: "playing" }).catch(console.warn);
						})
						.catch(console.error);
				}
			);
			
			await MediaSession.setActionHandler(
				{ action: "pause" },
				() => {
					console.log("[UnifiedMediaSession] Pause action");
					player.current?.pause()
						.then(() => {
							MediaSession.setPlaybackState({ playbackState: "paused" }).catch(console.warn);
						})
						.catch(console.error);
				}
			);
			
			await MediaSession.setActionHandler(
				{ action: "seekbackward" },
				(details) => {
					const currentTime = player.current?.currentTime || 0;
					const offset = (details as unknown as { seekOffset?: number }).seekOffset || seekTime;
					const newTime = Math.max(0, currentTime - offset);
					
					if (player.current) {
						player.current.currentTime = newTime;
						void updatePosition();
					}
				}
			);
			
			await MediaSession.setActionHandler(
				{ action: "seekforward" },
				(details) => {
					const currentTime = player.current?.currentTime || 0;
					const duration = player.current?.duration || 0;
					const offset = (details as unknown as { seekOffset?: number }).seekOffset || seekTime;
					const newTime = Math.min(duration, currentTime + offset);
					
					if (player.current) {
						player.current.currentTime = newTime;
						void updatePosition();
					}
				}
			);
			
			await MediaSession.setActionHandler(
				{ action: "seekto" },
				(details) => {
					if (details.seekTime != null && player.current) {
						const duration = player.current.duration || 0;
						const seekTo = Math.max(0, Math.min(duration, details.seekTime));
						player.current.currentTime = seekTo;
						void updatePosition();
					}
				}
			);
			
			// Android stop handler
			if (isAndroid) {
				await MediaSession.setActionHandler(
					{ action: "stop" },
					() => {
						console.log("[UnifiedMediaSession] Stop action");
						player.current?.pause().catch(console.error);
						MediaSession.setPlaybackState({ playbackState: "none" }).catch(console.warn);
					}
				);
			}
			
			// Start position polling
			const pollInterval = isAndroid ? 3000 : 5000;
			positionIntervalRef.current = window.setInterval(() => {
				void updatePosition();
			}, pollInterval);
			void updatePosition();
			
			console.log("[UnifiedMediaSession] Capacitor MediaSession configured");
			
			// Return cleanup function
			return async (): Promise<void> => {
				console.log("[UnifiedMediaSession] Cleaning up Capacitor MediaSession");
				
				if (positionIntervalRef.current) {
					clearInterval(positionIntervalRef.current);
					positionIntervalRef.current = null;
				}
				
				try {
					await MediaSession.setActionHandler({ action: "play" }, null);
					await MediaSession.setActionHandler({ action: "pause" }, null);
					await MediaSession.setActionHandler({ action: "seekbackward" }, null);
					await MediaSession.setActionHandler({ action: "seekforward" }, null);
					await MediaSession.setActionHandler({ action: "seekto" }, null);
					
					if (isAndroid) {
						await MediaSession.setActionHandler({ action: "stop" }, null);
					}
					
					await MediaSession.setPlaybackState({ playbackState: "none" });
				} catch (error) {
					console.warn("[UnifiedMediaSession] Cleanup error:", error);
				}
			};
		} catch (error) {
			console.error("[UnifiedMediaSession] Setup failed:", error);
			return null;
		}
	}, [isAndroid, player]);

	// ============================================
	// Web MediaSession API Implementation
	// ============================================

	const setupWebMediaSession = useCallback(
		(mediaElement: HTMLVideoElement | HTMLAudioElement) => {
			if (!navigator.mediaSession) {
				console.warn("[UnifiedMediaSession] Web MediaSession API not available");
				return null;
			}
			
			console.log("[UnifiedMediaSession] Setting up Web MediaSession API");
			
			try {
				// Set metadata
				navigator.mediaSession.metadata = new MediaMetadata({
					title: metadataRef.current.title,
					artist: metadataRef.current.artist || DEFAULT_ARTIST,
					album: metadataRef.current.album || DEFAULT_ALBUM,
					artwork: metadataRef.current.artwork || DEFAULT_ARTWORK,
				});
				
				// Update position helper
				const updatePosition = (): void => {
					if (!mediaElement) return;
					
					try {
						const duration = mediaElement.duration || 0;
						const position = mediaElement.currentTime || 0;
						const playbackRate = mediaElement.playbackRate || 1;
						
						if (duration > 0) {
							navigator.mediaSession?.setPositionState?.({
								duration,
								position,
								playbackRate,
							});
						}
					} catch (error) {
						console.warn("[UnifiedMediaSession] Position update failed:", error);
					}
				};
				
				// Seek time
				const seekTime = isAndroid ? 15 : 10;
				
				// Action handlers
				navigator.mediaSession.setActionHandler("play", () => {
					console.log("[UnifiedMediaSession] Play action");
					mediaElement.play()
						.then(() => {
							navigator.mediaSession.playbackState = "playing";
						})
						.catch(console.error);
				});
				
				navigator.mediaSession.setActionHandler("pause", () => {
					console.log("[UnifiedMediaSession] Pause action");
					mediaElement.pause();
					navigator.mediaSession.playbackState = "paused";
				});
				
			navigator.mediaSession.setActionHandler("seekbackward", (details: MediaSessionActionDetails) => {
				const offset = details.seekOffset ?? seekTime;
				mediaElement.currentTime = Math.max(0, mediaElement.currentTime - offset);
				updatePosition();
			});
			
			navigator.mediaSession.setActionHandler("seekforward", (details: MediaSessionActionDetails) => {
				const offset = details.seekOffset ?? seekTime;
				const duration = mediaElement.duration || 0;
				mediaElement.currentTime = Math.min(duration, mediaElement.currentTime + offset);
				updatePosition();
			});
			
			navigator.mediaSession.setActionHandler("seekto", (details: MediaSessionActionDetails) => {
				if (details.seekTime != null) {
					mediaElement.currentTime = details.seekTime;
					updatePosition();
				}
			});
				
				if (isAndroid) {
					navigator.mediaSession.setActionHandler("stop", () => {
						console.log("[UnifiedMediaSession] Stop action");
						mediaElement.pause();
						navigator.mediaSession.playbackState = "none";
					});
				}
				
				// iOS visibility handler
				let visibilityHandler: (() => void) | undefined;
				if (isIOS) {
					visibilityHandler = (): void => {
						if (document.hidden && !mediaElement.paused) {
							navigator.mediaSession.playbackState = "playing";
							updatePosition();
						}
					};
					document.addEventListener("visibilitychange", visibilityHandler);
				}
				
				// Position polling
				const pollInterval = isAndroid ? 3000 : 5000;
				positionIntervalRef.current = window.setInterval(updatePosition, pollInterval);
				updatePosition();
				
				console.log("[UnifiedMediaSession] Web MediaSession configured");
				
				// Return cleanup function
				return (): void => {
					console.log("[UnifiedMediaSession] Cleaning up Web MediaSession");
					
					if (positionIntervalRef.current) {
						clearInterval(positionIntervalRef.current);
						positionIntervalRef.current = null;
					}
					
					if (visibilityHandler) {
						document.removeEventListener("visibilitychange", visibilityHandler);
					}
					
					try {
						navigator.mediaSession?.setActionHandler("play", null);
						navigator.mediaSession?.setActionHandler("pause", null);
						navigator.mediaSession?.setActionHandler("seekbackward", null);
						navigator.mediaSession?.setActionHandler("seekforward", null);
						navigator.mediaSession?.setActionHandler("seekto", null);
						
						if (isAndroid) {
							navigator.mediaSession?.setActionHandler("stop", null);
						}
						
						if (navigator.mediaSession) {
							navigator.mediaSession.metadata = null;
							navigator.mediaSession.playbackState = "none";
						}
					} catch (error) {
						console.warn("[UnifiedMediaSession] Cleanup error:", error);
					}
				};
			} catch (error) {
				console.error("[UnifiedMediaSession] Setup failed:", error);
				return null;
			}
		},
		[isAndroid, isIOS]
	);

	// ============================================
	// Main Effect - Setup and Cleanup
	// ============================================

	useEffect(() => {
		if (!isActive) {
			// Cleanup if not active
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
			return;
		}
		
		if (!player.current) return;
		
		const setupSession = async (): Promise<void> => {
			if (useCapacitorPlugin) {
				// Use Capacitor plugin for native platforms
				const cleanup = await setupCapacitorMediaSession();
				if (cleanup) {
					cleanupRef.current = (): void => {
						cleanup().catch(console.warn);
					};
				}
			} else {
				// Use Web API - need to find media element
				const setupTimer = setTimeout(() => {
					const playerElement = player.current?.el;
					if (playerElement) {
						const mediaElement = playerElement.querySelector("video, audio") as
							| HTMLVideoElement
							| HTMLAudioElement;
						
						if (mediaElement) {
							cleanupRef.current = setupWebMediaSession(mediaElement);
						} else {
							console.warn("[UnifiedMediaSession] No media element found");
						}
					}
				}, 500); // Small delay for player to mount
				
				// Store timer cleanup in ref so it can be cleaned up properly
				const existingCleanup = cleanupRef.current;
				cleanupRef.current = (): void => {
					if (existingCleanup) existingCleanup();
					clearTimeout(setupTimer);
				};
			}
		};
		
		void setupSession();
		
		return (): void => {
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, [isActive, player, useCapacitorPlugin, setupCapacitorMediaSession, setupWebMediaSession]);

	// ============================================
	// Return Functions
	// ============================================

	const updatePlaybackState = useCallback(
		(state: "playing" | "paused" | "none"): void => {
			try {
				if (useCapacitorPlugin) {
					MediaSession.setPlaybackState({ playbackState: state }).catch(console.warn);
				} else if (navigator.mediaSession) {
					navigator.mediaSession.playbackState = state;
				}
				console.log(`[UnifiedMediaSession] Playback state: ${state}`);
			} catch (error) {
				console.warn(`[UnifiedMediaSession] Failed to update state to ${state}:`, error);
			}
		},
		[useCapacitorPlugin]
	);

	const updateMetadata = useCallback(
		(newMetadata: MediaSessionMetadata): void => {
			metadataRef.current = newMetadata;
			
			try {
				if (useCapacitorPlugin) {
					MediaSession.setMetadata({
						title: newMetadata.title,
						artist: newMetadata.artist || DEFAULT_ARTIST,
						album: newMetadata.album || DEFAULT_ALBUM,
						artwork: newMetadata.artwork || DEFAULT_ARTWORK,
					}).catch(console.warn);
				} else if (navigator.mediaSession) {
					navigator.mediaSession.metadata = new MediaMetadata({
						title: newMetadata.title,
						artist: newMetadata.artist || DEFAULT_ARTIST,
						album: newMetadata.album || DEFAULT_ALBUM,
						artwork: newMetadata.artwork || DEFAULT_ARTWORK,
					});
				}
			} catch (error) {
				console.warn("[UnifiedMediaSession] Failed to update metadata:", error);
			}
		},
		[useCapacitorPlugin]
	);

	const updatePositionState = useCallback((): void => {
		if (!player.current) return;
		
		try {
			const duration = player.current.duration || 0;
			const position = player.current.currentTime || 0;
			const playbackRate = player.current.playbackRate || 1;
			
			if (duration > 0) {
				if (useCapacitorPlugin) {
					MediaSession.setPositionState({
						duration,
						position,
						playbackRate,
					}).catch(console.warn);
				} else if (navigator.mediaSession?.setPositionState) {
					navigator.mediaSession.setPositionState({
						duration,
						position,
						playbackRate,
					});
				}
			}
		} catch (error) {
			console.warn("[UnifiedMediaSession] Failed to update position:", error);
		}
	}, [player, useCapacitorPlugin]);

	return {
		updatePlaybackState,
		updateMetadata,
		updatePositionState,
	};
}

// Re-export for compatibility
export { useUnifiedMediaSession as useMediaSessionUnified };
